import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Calendar, MapPin, Crown, Star, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface FeaturedEvent {
  id: string;
  slug: string;
  title: string;
  date: string;
  time: string;
  address: string;
  city: string | null;
  category: string | null;
  background_image_url: string;
  is_free: boolean;
}

interface FeaturedExperience {
  id: string;
  slug: string;
  name: string;
  address: string;
  experience_type: string;
  rating: number | null;
  price_range: string;
  image: string | null;
}

type FeaturedItem = { type: "event"; data: FeaturedEvent } | { type: "experience"; data: FeaturedExperience };

interface HomepageHeroProps {
  autoPlayInterval?: number;
  className?: string;
  listingType?: "event" | "experience" | "all";
}

export const HomepageHero: React.FC<HomepageHeroProps> = ({
  autoPlayInterval = 6000,
  className,
  listingType = "all",
}) => {
  const navigate = useNavigate();
  const [featuredItems, setFeaturedItems] = useState<FeaturedItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    fetchFeaturedItems();
  }, [listingType]);

  const fetchFeaturedItems = async () => {
    try {
      const items: FeaturedItem[] = [];
      const now = new Date().toISOString();

      // Fetch active homepage hero ad slots
      const { data: adSlots } = await supabase
        .from("ad_slots")
        .select("listing_id, listing_type")
        .eq("slot_location", "homepage_hero")
        .eq("status", "active")
        .lte("start_date", now)
        .gte("end_date", now);

      if (!adSlots || adSlots.length === 0) {
        setFeaturedItems([]);
        setLoading(false);
        return;
      }

      // Filter by listing type if specified
      const eventIds = adSlots
        .filter((slot) => slot.listing_type === "event" && (listingType === "event" || listingType === "all"))
        .map((slot) => slot.listing_id);

      const experienceIds = adSlots
        .filter((slot) => slot.listing_type === "experience" && (listingType === "experience" || listingType === "all"))
        .map((slot) => slot.listing_id);

      // Fetch events
      if (eventIds.length > 0) {
        const { data: eventsData } = await supabase
          .from("events")
          .select("id, slug, title, date, time, address, city, category, background_image_url, is_free")
          .eq("is_hidden", false)
          .in("id", eventIds);

        if (eventsData) {
          eventsData.forEach((event) => {
            items.push({ type: "event", data: event });
          });
        }
      }

      // Fetch experiences
      if (experienceIds.length > 0) {
        const { data: experiencesData } = await supabase
          .from("experiences")
          .select("id, slug, name, address, experience_type, rating, price_range, image")
          .eq("is_active", true)
          .in("id", experienceIds);

        if (experiencesData) {
          experiencesData.forEach((exp) => {
            items.push({ type: "experience", data: exp });
          });
        }
      }

      setFeaturedItems(items);
    } catch (error) {
      console.error("Error fetching featured items:", error);
    } finally {
      setLoading(false);
    }
  };

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % featuredItems.length);
  }, [featuredItems.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + featuredItems.length) % featuredItems.length);
  }, [featuredItems.length]);

  // Auto-play
  useEffect(() => {
    if (featuredItems.length <= 1 || isPaused) return;

    const interval = setInterval(goToNext, autoPlayInterval);
    return () => clearInterval(interval);
  }, [featuredItems.length, isPaused, autoPlayInterval, goToNext]);

  const handleItemClick = (item: FeaturedItem) => {
    if (item.type === "event") {
      navigate(`/event/${item.data.slug}`);
    } else {
      navigate(`/experiences/${item.data.slug}`);
    }
  };

  if (loading) {
    return (
      <div
        className={cn(
          "relative w-full h-[400px] md:h-[500px] rounded-2xl overflow-hidden bg-muted animate-pulse",
          className,
        )}
      />
    );
  }

  if (featuredItems.length === 0) {
    return null;
  }

  const currentItem = featuredItems[currentIndex];
  const imageUrl =
    currentItem.type === "event"
      ? currentItem.data.background_image_url
      : currentItem.data.image || "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200";

  return (
    <div
      className={cn("relative w-full h-[400px] md:h-[500px] rounded-2xl overflow-hidden group", className)}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
        style={{ backgroundImage: `url(${imageUrl})` }}
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

      {/* Content */}
      <div
        className="absolute inset-0 flex flex-col justify-end p-6 md:p-10 cursor-pointer"
        onClick={() => handleItemClick(currentItem)}
      >
        {/* Badges */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {/* Featured Badge */}
          <div className="px-3 py-1 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-lg">
            <Crown className="w-3 h-3" />
            FEATURED
          </div>

          {/* Type Badge */}
          <div className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-xs font-medium">
            {currentItem.type === "event" ? currentItem.data.category || "Event" : currentItem.data.experience_type}
          </div>

          {/* Free Badge for Events */}
          {currentItem.type === "event" && currentItem.data.is_free && (
            <div className="px-3 py-1 bg-green-600 text-white rounded-full text-xs font-semibold">FREE</div>
          )}
        </div>

        {/* Title */}
        <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-3 drop-shadow-lg">
          {currentItem.type === "event" ? currentItem.data.title : currentItem.data.name}
        </h2>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 text-white/90 text-sm md:text-base">
          {currentItem.type === "event" ? (
            <>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <span>{currentItem.data.date}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                <span>{currentItem.data.time}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                <span>{currentItem.data.city || currentItem.data.address.split(",")[0]}</span>
              </div>
            </>
          ) : (
            <>
              {currentItem.data.rating && currentItem.data.rating > 0 && (
                <div className="flex items-center gap-1.5">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span>{currentItem.data.rating.toFixed(1)}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <span>{currentItem.data.price_range}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                <span>{currentItem.data.address.split(",")[0]}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Navigation Arrows */}
      {featuredItems.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-full transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-full transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {featuredItems.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {featuredItems.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(index);
              }}
              className={cn(
                "w-2 h-2 rounded-full transition-all",
                index === currentIndex ? "bg-white w-6" : "bg-white/50 hover:bg-white/70",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};
